/* global debounce */

if (!customElements.get('search-form')) {
  class SearchForm extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.js-search-input');
      this.form = this.closest('form');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.inputHandler = debounce(this.handleInput.bind(this));
      this.submitHandler = this.handleSubmit.bind(this);
      this.productTypeChangeHandler = this.handleProductTypeChange.bind(this);
      this.resetHandler = this.handleReset.bind(this);
      this.addListeners();
      this.init();
    }

    disconnectedCallback() {
      this.input.removeEventListener('input', this.inputHandler);
      if (this.productTypeSelect) {
        this.productTypeSelect.removeEventListener('change', this.productTypeChangeHandler);
      }
      if (this.form && !this.closest('predictive-search')) {
        this.form.removeEventListener('submit', this.submitHandler);
      }
      this.resetBtn.removeEventListener('click', this.resetHandler);
    }

    addListeners() {
      this.input.addEventListener('input', this.inputHandler);
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.productTypeChangeHandler);
      }
      if (this.form && !this.closest('predictive-search')) {
        this.form.addEventListener('submit', this.submitHandler);
      }
      this.resetBtn.addEventListener('click', this.resetHandler);
    }

    init() {
      if ((this.input.dataset.placeholderTwo || this.input.dataset.placeholderThree)
        && (this.input.dataset.placeholderPromptsMob === 'true' || theme.mediaMatches.md)) {
        this.typeInPlaceholders();
      }
    }

    /**
     * Simulates the placeholder titles being typed in and the previous one deleted, on repeat
     * ✅ MINIMAL FIX: Only cache .length to prevent forced reflows
     */
    typeInPlaceholders() {
      const typingSpeed = this.input.dataset.typingSpeed || 100;
      const deletingSpeed = this.input.dataset.deletingSpeed || 60;
      const delayAfterDeleting = this.input.dataset.delayAfterDeleting || 500;
      const delayBeforeFirstDelete = this.input.dataset.delayBeforeFirstDelete || 2000;
      const delayAfterWordTyped = this.input.dataset.delayAfterWordTyped || 2400;
      const placeholders = [];

      if (this.input.placeholder) {
        placeholders.push(this.input.placeholder);
      }

      if (this.input.dataset.placeholderTwo) {
        placeholders.push(this.input.dataset.placeholderTwo);
      }

      if (this.input.dataset.placeholderThree) {
        placeholders.push(this.input.dataset.placeholderThree);
      }

      const typeInNextPlaceholder = async (placeholder) => {
        await new Promise((resolve) => {
          let currentPlaceholder = this.input.getAttribute('placeholder');
          
          // ✅ FIX: Cache length once before the loop
          const currentPlaceholderLength = currentPlaceholder.length;

          // If necessary remove the start of the word to type in if it already exists in the
          // placeholder
          let nextPlaceholder = (currentPlaceholderLength >= 3
              && placeholder.startsWith(currentPlaceholder))
            ? placeholder.replace(currentPlaceholder, '')
            : placeholder;

          const typingIntervalId = setInterval(() => {
            currentPlaceholder = this.input.getAttribute('placeholder');
            this.input.setAttribute('placeholder', currentPlaceholder + nextPlaceholder.charAt(0));
            
            // ✅ FIX: Cache length to avoid reading it during comparison
            const nextLength = nextPlaceholder.length;
            if (nextLength === 1) {
              resolve();
              clearInterval(typingIntervalId); // Stop typing
            } else {
              nextPlaceholder = nextPlaceholder.substring(1);
            }
          }, typingSpeed);
        });
      };

      const deleteCurrentPlaceholder = async (nextPlaceholder) => {
        await new Promise((resolve) => {
          let prevPlaceholder = this.input.getAttribute('placeholder');
          
          const deletionIntervalId = setInterval(() => {
            // ✅ FIX: Cache length before using it
            const prevLength = prevPlaceholder.length;
            const newPlaceholder = prevPlaceholder.substring(0, prevLength - 1);
            this.input.setAttribute('placeholder', newPlaceholder);
            prevPlaceholder = newPlaceholder;
            
            // ✅ FIX: Cache the new length
            const newLength = prevPlaceholder.length;
            if (newLength === 0
              || (newLength >= 3 && nextPlaceholder.startsWith(prevPlaceholder))) {
              resolve();
              clearInterval(deletionIntervalId);
            }
          }, deletingSpeed);
        });
      };

      let startIndex = 0;
      const showNextPlaceholder = () => {
        startIndex = (startIndex + 1) % placeholders.length;
        const nextPlaceholder = placeholders[startIndex];
        deleteCurrentPlaceholder.call(this, nextPlaceholder).then(() => {
          setTimeout(() => {
            typeInNextPlaceholder.call(this, nextPlaceholder).then(() => {
              setTimeout(showNextPlaceholder.bind(this), delayAfterWordTyped);
            });
          }, delayAfterDeleting);
        });
      };

      setTimeout(showNextPlaceholder.bind(this), delayBeforeFirstDelete);
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      if (this.input.value.length > 0) {
        this.input.classList.add('search__input--dirty');
      } else {
        this.input.classList.remove('search__input--dirty');
      }
    }

    /**
     * Handles search form submit.
     */
    handleSubmit() {
      this.input.value = theme.search.normalizeSearchTerm(this.input.value);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
    }

    /**
     * Handles the reset button being clicked
     */
    handleReset() {
      this.input.classList.remove('search__input--dirty');
    }
  }

  customElements.define('search-form', SearchForm);
}
